import React, { useMemo } from 'react';

import { TranslateBadge } from '@travel/icons/ui';
import { Skeleton } from '@travel/ui';

import TimeLine from 'components/TimeLine';

import { Translate } from 'core/translate';
import useServerTranslation from 'hooks/useServerTranslation';
import type { CancellationPenalties, Price } from 'ProviderRatePlan-Types';
import { isRspDirectOffer } from 'utils/salesPromotion';

import styles from './cancellationPolicy.module.scss';

type Props = {
  wrapperClassName: string;
  titleClassName: string;
  cancellationPenalties: Array<CancellationPenalties>;
  currency: string;
  language: string;
  price: Price;
  cancellationRemarks?: string;
  isDirectContract: boolean;
  isOpenCancellationPolicyPopup?: boolean;
};

function CancellationPolicy(props: Props) {
  const {
    wrapperClassName,
    titleClassName,
    cancellationPenalties,
    currency,
    language,
    price,
    cancellationRemarks,
    isDirectContract,
    isOpenCancellationPolicyPopup,
  } = props;

  const memoizedCancellationRemark = useMemo(() => {
    return [{ seqNo: 1, text: cancellationRemarks || '' }];
  }, [cancellationRemarks]);

  const shouldTranslate = isOpenCancellationPolicyPopup && !!cancellationRemarks;

  const { isLoading, translatedText, translationType } = useServerTranslation(
    memoizedCancellationRemark,
    shouldTranslate,
  );

  const remark =
    translatedText && translatedText?.length > 0 ? translatedText[0]?.text : cancellationRemarks;

  const isGoogleTranslated = translationType === 'AUTO_TRANSLATED';
  const isRspDirectOfferApplicable = isRspDirectOffer(price?.salesPromotions);
  const cancellationPolicyDisclaimerId = useMemo(() => {
    if (isRspDirectOfferApplicable) {
      return 'Provider_Info.Cancel_Policy_Summary.Cancellation_Disclaimer_SP_Item_popup';
    }
    if (isDirectContract) {
      return 'Provider_Info.Room_List.Plan_Details.Price_Disclaimer';
    }
    return 'Cancellation_Policy_Common.Out_To_Out.Cancellation_Policy_Modal.Disclaimer.Sales';
  }, [isRspDirectOfferApplicable, isDirectContract]);

  return (
    <div className={wrapperClassName} data-testid="cancellationPolicy-wrapper">
      <div className={titleClassName}>
        <Translate id="Cancellation_Policy_Common.Cancellation_Policy_Modal.Section_Title" />
      </div>
      <TimeLine
        cancellationPenalties={cancellationPenalties}
        className={styles.timeline}
        reservationPrice={price.total}
        currency={currency}
        locale={language}
        isIndirectContract={!isDirectContract}
      />
      {isLoading ? (
        <Skeleton row={5} type="paragraph" />
      ) : (
        <>
          <Translate className={styles.disclaimer} id={cancellationPolicyDisclaimerId} />
          <Translate
            className={styles.disclaimer}
            id={
              isDirectContract
                ? 'Provider_Info.Room_List.Plan_Details.Rate_Disclaimer'
                : 'Cancellation_Policy_Common.Out_To_Out.Cancellation_Policy_Modal.Disclaimer.Time_Zone'
            }
          />
          {!isDirectContract && (
            <>
              <Translate
                className={styles.disclaimer}
                id={
                  'Cancellation_Policy_Common.Out_To_Out.Cancellation_Policy_Modal.Disclaimer.Cancel_Request'
                }
              />
              <Translate
                className={styles.disclaimer}
                id="Cancellation_Policy_Common.Out_To_Out.Cancellation_Policy.Non_Partial_Cancellation"
              />
              <Translate
                className={styles.disclaimer}
                id="Cancellation_Policy_Common.Out_To_Out.Cancellation_Policy.Pending_Status_Notice"
              />
            </>
          )}

          {cancellationRemarks && (
            <>
              <Translate
                className={styles.disclaimer}
                id="Cancellation_Policy_Common.Out_To_Out.Cancellation_Policy.EarlyLateCheckIn_Notice"
              />
              <p className={styles.disclaimer} data-testid="translated-remarks">
                {remark}
              </p>
              {isGoogleTranslated && (
                <TranslateBadge
                  className={styles.translateBadge}
                  data-testid="cancellationRemark-translateBadge"
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default CancellationPolicy;
